import { ArrowLeft, Shield, Lock, Eye, Database, Users, Globe, Mail, AlertCircle } from "lucide-react";
import { BottomNav } from "./bottom-nav";
import { requestOpenPreferences } from "@/app/consent/consent-context";

/** Logged-in shell (settings → política). */
export type PrivacyPolicyEmbeddedProps = {
  publicStandalone?: false;
  onBack: () => void;
  activeTab: "home" | "favorites" | "wallet";
  onTabChange: (tab: string) => void;
  favoritesCount: number;
  onMenuClick: () => void;
  onPostAdClick: () => void;
  postAdBlockedByUnverifiedEmail?: boolean;
};

/** Standalone public URL (`/privacidade`, `/privacy-policy`) — no bottom nav, sem sessão. */
export type PrivacyPolicyPublicProps = {
  publicStandalone: true;
};

export type PrivacyPolicyPageProps = PrivacyPolicyEmbeddedProps | PrivacyPolicyPublicProps;

/** Rotas públicas suportadas em `main.tsx` (SPA + mesmo path no hosting). */
export const PUBLIC_PRIVACY_POLICY_PATHS = ["/privacidade", "/privacy-policy"] as const;

export function PrivacyPolicyPage(props: PrivacyPolicyPageProps) {
  const isPublic = props.publicStandalone === true;
  const onBack = isPublic
    ? () => {
        window.location.assign("/");
      }
    : props.onBack;

  return (
    <div className={`min-h-screen bg-background ${isPublic ? "pb-8" : "pb-24"}`}>
      {/* Header */}
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 px-4 py-4">
            <button
              type="button"
              onClick={onBack}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors -ml-2"
              aria-label={isPublic ? "Ir ao início" : "Voltar"}
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>
            <h1 className="text-lg font-medium text-white">Política de Privacidade</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* LGPD Badge */}
        <div className="bg-secondary/30 border border-primary/20 rounded-xl p-4 mb-6">
          <div className="flex gap-3">
            <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-foreground mb-1">Conformidade com a LGPD</h3>
              <p className="text-sm text-muted-foreground">
                Esta política está em conformidade com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018)
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Última atualização: 19 de Janeiro de 2026
              </p>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <div className="prose prose-sm max-w-none mb-6">
          <p className="text-sm text-muted-foreground leading-relaxed">
            No akihub, levamos sua privacidade muito a sério. Esta Política de Privacidade 
            descreve como coletamos, usamos, armazenamos e protegemos seus dados pessoais.
          </p>
        </div>

        {/* Policy Sections */}
        <div className="space-y-6">
          {/* Section 1 */}
          <section className="bg-card border border-border rounded-xl p-4">
            <div className="flex gap-3 mb-3">
              <Database className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <h2 className="font-medium text-foreground">1. Dados Coletados</h2>
            </div>
            <div className="ml-8">
              <h3 className="text-sm font-medium text-foreground mb-2">Dados Fornecidos por Você:</h3>
              <ul className="space-y-1 text-sm text-muted-foreground mb-3">
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span>Nome completo e CPF</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span>Endereço de e-mail e telefone</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span>Endereço de entrega e cobrança</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span>Informações de pagamento (processadas de forma segura)</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span>Fotos e descrições de produtos</span>
                </li>
              </ul>
              
              <h3 className="text-sm font-medium text-foreground mb-2">Dados Coletados Automaticamente:</h3>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span>Endereço IP e localização aproximada</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span>Tipo de dispositivo e sistema operacional</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span>Páginas visitadas e tempo de navegação</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span>Histórico de buscas e preferências</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span>Cookies e tecnologias similares</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 2 */}
          <section className="bg-card border border-border rounded-xl p-4">
            <div className="flex gap-3 mb-3">
              <Eye className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <h2 className="font-medium text-foreground">2. Como Usamos Seus Dados</h2>
            </div>
            <div className="ml-8">
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Prestação de Serviços:</strong> Processar transações, gerenciar conta e facilitar compra/venda</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Comunicação:</strong> Enviar confirmações, atualizações e suporte ao cliente</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Personalização:</strong> Oferecer recomendações baseadas em seus interesses</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Segurança:</strong> Prevenir fraudes e garantir a segurança da plataforma</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Melhorias:</strong> Analisar uso e melhorar funcionalidades</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 3 */}
          <section className="bg-card border border-border rounded-xl p-4">
            <div className="flex gap-3 mb-3">
              <Lock className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <h2 className="font-medium text-foreground">3. Proteção de Dados</h2>
            </div>
            <div className="ml-8">
              <p className="text-sm text-muted-foreground mb-3">
                Implementamos medidas técnicas e organizacionais para proteger seus dados:
              </p>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span>Criptografia SSL/TLS para transmissão de dados</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span>Armazenamento seguro em servidores protegidos</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span>Controle de acesso restrito a dados pessoais</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span>Monitoramento contínuo de segurança</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span>Auditorias regulares de segurança</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span>Treinamento de equipe em proteção de dados</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 4 */}
          <section className="bg-card border border-border rounded-xl p-4">
            <div className="flex gap-3 mb-3">
              <Users className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <h2 className="font-medium text-foreground">4. Compartilhamento de Dados</h2>
            </div>
            <div className="ml-8">
              <p className="text-sm text-muted-foreground mb-3">
                Seus dados podem ser compartilhados apenas nas seguintes situações:
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Com outros usuários:</strong> Informações necessárias para transações (nome, localização)</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Processadores de pagamento:</strong> Para processar transações financeiras</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Transportadoras:</strong> Para entrega de produtos</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Provedores de serviços:</strong> Hospedagem, análise e suporte técnico</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Autoridades legais:</strong> Quando exigido por lei ou ordem judicial</span>
                </li>
              </ul>
              <p className="text-sm text-muted-foreground mt-3">
                <strong className="text-foreground">Importante:</strong> Nunca vendemos seus dados pessoais a terceiros.
              </p>
            </div>
          </section>

          {/* Section 5 */}
          <section className="bg-card border border-border rounded-xl p-4">
            <div className="flex gap-3 mb-3">
              <Globe className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <h2 className="font-medium text-foreground">5. Cookies e Tecnologias Similares</h2>
            </div>
            <div className="ml-8">
              <p className="text-sm text-muted-foreground mb-3">
                Utilizamos cookies para melhorar sua experiência:
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Essenciais:</strong> Necessários para funcionamento básico</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Funcionais:</strong> Lembrar preferências e configurações</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Analíticos:</strong> Entender como você usa o aplicativo</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Marketing:</strong> Exibir anúncios relevantes (com consentimento)</span>
                </li>
              </ul>
              <p className="text-sm text-muted-foreground mt-3">
                Você pode{' '}
                <button
                  type="button"
                  onClick={requestOpenPreferences}
                  className="text-primary underline-offset-2 hover:underline focus:outline-none focus-visible:underline"
                >
                  gerenciar suas preferências de cookies
                </button>
                {' '}a qualquer momento, ou ajustar nas configurações do seu navegador.
              </p>
            </div>
          </section>

          {/* Section 6 */}
          <section className="bg-card border border-border rounded-xl p-4">
            <div className="flex gap-3 mb-3">
              <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <h2 className="font-medium text-foreground">6. Seus Direitos (LGPD)</h2>
            </div>
            <div className="ml-8">
              <p className="text-sm text-muted-foreground mb-3">
                Conforme a LGPD, você tem direito a:
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Confirmação:</strong> Saber se tratamos seus dados</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Acesso:</strong> Obter cópia dos seus dados</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Correção:</strong> Atualizar dados incompletos ou incorretos</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Anonimização/Bloqueio:</strong> Limitar uso dos dados</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Eliminação:</strong> Excluir dados desnecessários</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Portabilidade:</strong> Receber dados em formato estruturado</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Informação:</strong> Saber sobre compartilhamento</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Revogação:</strong> Retirar consentimento a qualquer momento</span>
                </li>
              </ul>
              <p className="text-sm text-muted-foreground mt-3">
                Para exercer seus direitos, acesse as Configurações de Privacidade ou entre em contato conosco.
              </p>
            </div>
          </section>

          {/* Section 7 */}
          <section className="bg-card border border-border rounded-xl p-4">
            <div className="flex gap-3 mb-3">
              <Database className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <h2 className="font-medium text-foreground">7. Retenção de Dados</h2>
            </div>
            <div className="ml-8">
              <p className="text-sm text-muted-foreground mb-3">
                Mantemos seus dados pelo tempo necessário para:
              </p>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span>Prestar nossos serviços</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span>Cumprir obrigações legais e fiscais</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary flex-shrink-0">•</span>
                  <span>Resolver disputas e aplicar nossos termos</span>
                </li>
              </ul>
              <p className="text-sm text-muted-foreground mt-3">
                Dados de transações são mantidos por 5 anos conforme legislação fiscal brasileira.
              </p>
            </div>
          </section>

          {/* Section 8 */}
          <section className="bg-card border border-border rounded-xl p-4">
            <div className="flex gap-3 mb-3">
              <Users className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <h2 className="font-medium text-foreground">8. Dados de Menores</h2>
            </div>
            <div className="ml-8">
              <p className="text-sm text-muted-foreground">
                O akihub não é destinado a menores de 18 anos. Não coletamos 
                intencionalmente dados de crianças ou adolescentes. Se tomarmos conhecimento 
                de que coletamos dados de um menor, excluiremos as informações imediatamente.
              </p>
            </div>
          </section>

          {/* Section 9 */}
          <section className="bg-card border border-border rounded-xl p-4">
            <div className="flex gap-3 mb-3">
              <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <h2 className="font-medium text-foreground">9. Alterações nesta Política</h2>
            </div>
            <div className="ml-8">
              <p className="text-sm text-muted-foreground">
                Podemos atualizar esta Política de Privacidade periodicamente. Alterações 
                significativas serão notificadas por e-mail ou aviso no aplicativo. 
                Recomendamos revisar esta política regularmente.
              </p>
            </div>
          </section>

          {/* Section 10 */}
          <section className="bg-card border border-border rounded-xl p-4">
            <div className="flex gap-3 mb-3">
              <Mail className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <h2 className="font-medium text-foreground">10. Contato e DPO</h2>
            </div>
            <div className="ml-8">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Os dados de contato do Encarregado de Proteção de Dados (DPO) e os canais
                oficiais para questões sobre privacidade e exercício dos seus direitos serão
                publicados nesta seção em breve.
              </p>
            </div>
          </section>
        </div>

        {/* Final Notice */}
        <div className="mt-6 bg-primary/10 border border-primary/20 rounded-xl p-4">
          <div className="flex gap-3">
            <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-foreground mb-2">Seu Consentimento</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Ao usar o akihub, você consente com a coleta e uso de informações 
                conforme descrito nesta Política de Privacidade. Você pode retirar seu 
                consentimento a qualquer momento através das configurações de privacidade.
              </p>
            </div>
          </div>
        </div>
      </div>

      {!isPublic ? (
        <BottomNav
          activeTab={props.activeTab}
          onTabChange={props.onTabChange}
          favoritesCount={props.favoritesCount}
          onMenuClick={props.onMenuClick}
          onPostAdClick={props.onPostAdClick}
          postAdBlockedByUnverifiedEmail={props.postAdBlockedByUnverifiedEmail ?? false}
        />
      ) : null}
    </div>
  );
}